import React, { useState } from 'react';
import { AlertTriangle, Play, RotateCcw, TrendingDown, Clock, Target, Layers } from 'lucide-react';
import { DependencyGraphNode, ImpactSimulation } from '../../types';
import { apiService } from '../../services/api';

interface ImpactSimulatorProps {
  availableGoals: DependencyGraphNode[];
}

export const ImpactSimulator: React.FC<ImpactSimulatorProps> = ({ availableGoals }) => {
  const [selectedGoal, setSelectedGoal] = useState<string>('');
  const [delayDays, setDelayDays] = useState<number>(7);
  const [simulation, setSimulation] = useState<ImpactSimulation | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleRunSimulation = async () => {
    if (!selectedGoal || delayDays <= 0) {
      setError('Please select a goal and enter a valid delay period');
      return;
    }

    try {
      setLoading(true);
      setError('');
      const result = await apiService.simulateImpact(selectedGoal, delayDays);
      setSimulation(result);
    } catch (err) {
      console.error('Failed to run impact simulation:', err);
      setError('Failed to run impact simulation');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setSimulation(null);
    setSelectedGoal('');
    setDelayDays(7);
    setError('');
  };

  const getImpactColor = (impactPercentage: number) => {
    if (impactPercentage >= 80) return 'text-red-600 bg-red-50 border-red-200';
    if (impactPercentage >= 60) return 'text-orange-600 bg-orange-50 border-orange-200';
    if (impactPercentage >= 40) return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    return 'text-blue-600 bg-blue-50 border-blue-200';
  };

  const getImpactIcon = (impactType: string) => {
    switch (impactType) {
      case 'blocked':
        return <AlertTriangle className="w-4 h-4 text-red-500" />;
      case 'delayed':
        return <Clock className="w-4 h-4 text-orange-500" />;
      case 'reduced_efficiency':
        return <TrendingDown className="w-4 h-4 text-yellow-500" />;
      default:
        return <Target className="w-4 h-4 text-blue-500" />;
    }
  };

  const formatImpactType = (type: string) => {
    return type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  return (
    <div className="space-y-6">
      {/* Input Section */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-orange-600" />
          Ripple Effect Simulator
        </h3>

        <p className="text-gray-600 mb-6">
          Simulate how delays in one goal affect all dependent goals across your projects
        </p>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Goal to Delay
            </label>
            <select
              value={selectedGoal}
              onChange={(e) => setSelectedGoal(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={loading}
            >
              <option value="">Choose a goal...</option>
              {availableGoals.map((goal) => (
                <option key={goal.id} value={goal.id}>
                  {goal.label} ({goal.projectName})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Delay Period (Days)
            </label>
            <input
              type="number"
              min="1"
              max="365"
              value={delayDays}
              onChange={(e) => setDelayDays(parseInt(e.target.value) || 0)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={loading}
            />
          </div>
        </div>

        <div className="flex gap-3">
          <button
            onClick={handleRunSimulation}
            disabled={loading || !selectedGoal || delayDays <= 0}
            className="btn-primary flex items-center gap-2"
          >
            <Play className="w-4 h-4" />
            {loading ? 'Running Simulation...' : 'Run Simulation'}
          </button>
          <button
            onClick={handleReset}
            className="btn-secondary flex items-center gap-2"
          >
            <RotateCcw className="w-4 h-4" />
            Reset
          </button>
        </div>
      </div>

      {/* Simulation Results */}
      {simulation && (
        <div className="space-y-6">
          {/* Summary */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h4 className="text-lg font-semibold text-gray-900 mb-4">
              Impact Analysis Summary
            </h4>

            <div className="bg-red-50 border border-red-200 p-4 rounded-lg mb-6">
              <div className="flex items-center gap-3 mb-2">
                <AlertTriangle className="w-5 h-5 text-red-600" />
                <span className="font-semibold text-red-900">
                  Delaying "{simulation.originalGoal.name}" by {simulation.delayDays} days
                </span>
              </div>
              <p className="text-red-800 text-sm">
                This delay will have cascading effects across {simulation.totalAffectedProjects} project(s) 
                and impact {simulation.totalAffectedGoals} additional goal(s).
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center gap-2 text-gray-600 mb-1">
                  <Target className="w-4 h-4" />
                  Affected Goals
                </div>
                <div className="text-2xl font-bold text-gray-900">
                  {simulation.totalAffectedGoals}
                </div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center gap-2 text-gray-600 mb-1">
                  <Layers className="w-4 h-4" />
                  Affected Projects
                </div>
                <div className="text-2xl font-bold text-gray-900">
                  {simulation.totalAffectedProjects}
                </div>
              </div>
              <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
                <div className="flex items-center gap-2 text-red-600 mb-1">
                  <AlertTriangle className="w-4 h-4" />
                  Critical Impact
                </div>
                <div className="text-2xl font-bold text-red-900">
                  {simulation.affectedGoals.filter(g => g.impactPercentage >= 80).length}
                </div>
              </div>
              <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                <div className="flex items-center gap-2 text-blue-600 mb-1">
                  <Clock className="w-4 h-4" />
                  Max Delay
                </div>
                <div className="text-2xl font-bold text-blue-900">
                  {Math.max(...simulation.affectedGoals.map(g => g.estimatedDelay), 0)} days
                </div>
              </div>
            </div>
          </div>

          {/* Project-Level Impacts */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h4 className="text-lg font-semibold text-gray-900 mb-4">
              Project-Level Impact
            </h4>

            <div className="space-y-4">
              {simulation.projectImpacts.map((projectImpact) => (
                <div key={projectImpact.projectId} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h5 className="font-semibold text-gray-900">
                      {projectImpact.projectName}
                    </h5>
                    <div className={`px-3 py-1 rounded-full text-sm font-medium ${getImpactColor(projectImpact.maxImpact)}`}>
                      Max Impact: {projectImpact.maxImpact}%
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">Affected Goals:</span>
                      <div className="font-semibold text-gray-900">
                        {projectImpact.affectedGoalsCount}
                      </div>
                    </div>
                    <div>
                      <span className="text-gray-600">Avg Impact:</span>
                      <div className="font-semibold text-gray-900">
                        {projectImpact.avgImpact}%
                      </div>
                    </div>
                    <div>
                      <span className="text-gray-600">Total Delay:</span>
                      <div className="font-semibold text-gray-900">
                        {projectImpact.totalEstimatedDelay} days
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Affected Goals Details */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h4 className="text-lg font-semibold text-gray-900 mb-4">
              Affected Goals Details
            </h4>

            <div className="space-y-3">
              {simulation.affectedGoals.map((goal) => (
                <div 
                  key={goal.id} 
                  className={`border rounded-lg p-4 ${getImpactColor(goal.impactPercentage)}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        {getImpactIcon(goal.impactType)}
                        <h6 className="font-semibold text-gray-900">
                          {goal.name}
                        </h6>
                        <span className="text-sm text-gray-600">
                          ({goal.projectName})
                        </span>
                      </div>

                      <div className="flex items-center gap-4 text-sm mb-2">
                        <div>
                          <span className="font-medium">Impact Type:</span>
                          <span className="ml-1">{formatImpactType(goal.impactType)}</span>
                        </div>
                        <div>
                          <span className="font-medium">Depth:</span>
                          <span className="ml-1">{goal.depth} level{goal.depth !== 1 ? 's' : ''} removed</span>
                        </div>
                        <div>
                          <span className="font-medium">Est. Delay:</span>
                          <span className="ml-1">{goal.estimatedDelay} days</span>
                        </div>
                      </div>

                      {goal.targetDate && (
                        <div className="text-sm text-gray-600">
                          <span className="font-medium">Target Date:</span>
                          <span className="ml-1">{new Date(goal.targetDate).toLocaleDateString()}</span>
                        </div>
                      )}
                    </div>

                    <div className="text-right">
                      <div className="text-lg font-bold">
                        {goal.impactPercentage}%
                      </div>
                      <div className="text-xs text-gray-500">
                        Impact
                      </div>
                    </div>
                  </div>

                  {/* Impact Progress Bar */}
                  <div className="mt-3">
                    <div className="w-full bg-white bg-opacity-50 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all duration-500 ${
                          goal.impactPercentage >= 80 ? 'bg-red-500' :
                          goal.impactPercentage >= 60 ? 'bg-orange-500' :
                          goal.impactPercentage >= 40 ? 'bg-yellow-500' :
                          'bg-blue-500'
                        }`}
                        style={{ width: `${Math.min(goal.impactPercentage, 100)}%` }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

